import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { useT, useLocale, apiLang } from "../lib/i18n";
import { Button } from "./ui/Button";

/**
 * Passwordless email flow: request a 6-digit code, then redeem it.
 * Logs in existing accounts and creates new ones (server decides), so the
 * same component serves both the login and register pages.
 */
export function EmailCodeAuth({ onAuthed }: { onAuthed: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const [phase, setPhase] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.requestLoginCode(email.trim().toLowerCase(), apiLang(locale));
      setCode("");
      setPhase("code");
    } catch (e) {
      if (e instanceof ApiError && e.message === "code_recently_sent") {
        // A live code is already in their inbox — let them type it.
        setErr(t("auth.codeCooldown"));
        setPhase("code");
      } else {
        setErr(t("auth.genericError"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function redeem(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.verifyLoginCode(email.trim().toLowerCase(), code.trim());
      onAuthed();
    } catch (e) {
      setErr(
        e instanceof ApiError && e.message === "invalid_code"
          ? t("auth.codeInvalid")
          : t("auth.genericError"),
      );
      setBusy(false);
    }
  }

  if (phase === "email") {
    return (
      <form onSubmit={sendCode} className="w-full space-y-3">
        <input
          className="input"
          type="email"
          required
          placeholder={t("auth.email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoFocus
        />
        {err && <p className="text-sm text-danger">{err}</p>}
        <Button type="submit" disabled={busy} fullWidth>
          {t("auth.sendCode")}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={redeem} className="w-full space-y-3">
      <p className="text-sm text-fg-muted text-center">{t("auth.codeSent", { email: email.trim() })}</p>
      <input
        className="input text-center tracking-[0.4em] font-medium"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        required
        placeholder={t("auth.codePlaceholder")}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        autoComplete="one-time-code"
        autoFocus
      />
      {err && <p className="text-sm text-danger text-center">{err}</p>}
      <Button type="submit" disabled={busy || code.length !== 6} fullWidth>
        {t("auth.codeCta")}
      </Button>
      <button type="button" onClick={() => sendCode()} disabled={busy} className="w-full text-sm text-fg-muted press">
        {t("auth.codeResend")}
      </button>
    </form>
  );
}
